import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import _ from 'lodash';

import { RootState } from '../../../../configs/store';
import Icon from '../../../Icon/Icon';
import formatOdds from '../../../../utils/formatOdds';
import CombinationsModal from 'src/components/NumbersBetting/LottoSummary/CombinationsModal/CombinationsModal';
import { Bet } from 'src/interfaces/BetslipState';
import { toRoman } from 'src/utils/utils';
import BallsContainer from 'src/components/Betslip/NumberBet/Components/BallsContainer/BallsContainer';

interface betHistoryProp {
  bet: Bet,
  closeAll: boolean,
  isTicketDetail?: boolean,
  isSettled?: boolean,
  showHeader?: boolean,
  showTournament?: boolean
}

export default function BetHistoryRow({ bet,
  closeAll,
  isTicketDetail = false,
  isSettled = false,
  showHeader=true,
  showTournament=true
}: betHistoryProp) {
  const [showMore, setShowMore] = useState(false);
  const [showCombinations, setShowCombinations] = useState(false);
  const { selectedOddsType, selectedTimezone } = useSelector((state: RootState) => state.odds);
  const date = bet.startTime && utcToZonedTime(parseInt(bet.startTime) * 1000, selectedTimezone);

  const { t } = useTranslation();

  useEffect(() => {
    setShowMore(closeAll);
  }, [closeAll]);

  const status = bet.statusLabel === 'paided_out' ? 'won': _.camelCase(bet.statusLabel ? bet.statusLabel : bet.statusName);
  const tournamentName = bet.tournament?.name ? `${ bet.tournament?.name }, ` : '';
  const name = bet.productType === 'horses' ? bet?.meetingName : tournamentName ;

  const jsonLabel = bet.jsonLabel ? (typeof bet.jsonLabel === 'object') ? bet.jsonLabel : JSON.parse(bet.jsonLabel) : {};

  const renderDeadHeat = (bet) => {
    return (
      <>
        {
          (bet.rule4Enabled && bet?.statusLabel === 'won' && bet?.oddsType === 'outcome' && bet.rule4Deduction) ?
            <span className="betting-history__label">
              { t('accountHistory.rule4') }: { bet.rule4Deduction }%
            </span>
            :
            <span className="betting-history__label">
              { t('accountHistory.rule4') }
            </span>
        }
        <span className="betting-history__label">
          { `${ t('accountHistory.deadHeat') } ${ bet.voidFactor }` }
        </span>
      </>
    );
  };

  const renderVoid = (bet) => {
    let status = '';

    if (bet?.statusLabel === 'half_stake_back') {
      status = t('accountHistory.lost');
    }

    if (bet?.statusLabel === 'half_stake_back_half_win') {
      status = t('accountHistory.won');
    }

    return (
      <>
        <span className="betting-history__label">
          { `${ t('accountHistory.void') } ${ bet.voidFactor } ${ status }` }
        </span>
        {
          bet?.adjustmentText && bet?.statusLabel !== 'bet_cancelled' && (
            <span className="betting-history__label">
              { bet.adjustmentText }
            </span>
          )
        }
      </>
    );
  };

  const renderRule4Deduction = (bet) => {
    return (
      <span className="betting-history__label">
        {
          bet.rule4Deduction && bet.status === 2 && (+(bet.voidFactor) > 1 || bet.status === 2) ?
            `${ t('accountHistory.rule4') }: ${ bet.rule4Deduction }%`
            :
            t('accountHistory.rule4')
        }
      </span>
    );
  };

  const renderCancelled = () => {
    const label = bet?.adjustmentText ?
      `${ t('accountHistory.cancelled') }: ${ bet.adjustmentText }` : t('accountHistory.cancelled');

    return (
      <span className="betting-history__label">
        { label }
      </span>
    );
  };

  const renderLabelName = (label: string) => (
    <div className="betting-history__label-name">
      { label }
    </div>
  );

  return (
    <div
      className={
        classNames({
          'betting-history__more--open': showMore || isTicketDetail
        })
      }
    >
      {
        showHeader &&
        <>
          {
            showTournament  &&
            <span className="betting-history__tournament-name">{ `${ name }, ${ bet.tournamentGroup?.name ?? '' }` }</span>
          }
          <span className="betting-history__event-name">{ bet.eventName }</span>
          <span className="betting-history__start-time">{ date && format(date, 'dd/MM / HH:mm') }</span>
        </>
      }
      <div
        className="betting-history__item"
      >
        <div className="betting-history__container betting-history__container--wrap">
          <span className="betting-history__type-number">{ bet.line }.</span>
          <span className="betting-history__type-name">{ bet.marketDescription }:</span>
          {
            typeof jsonLabel !== 'object' &&
             <span className="betting-history__name">{
               jsonLabel
             }</span>
          }
          {
            bet.selectionType && (
              <span className="betting-history__selection-type">{ bet.selectionType } -</span>
            )
          }
          {
            (bet.productType === 'horses' && (bet.prediction && bet.prediction?.numbersToPredictCount > 1 || bet.oddsType === 'tote')) &&
            <div>
              {
                _.map(jsonLabel, (value, key) => {
                  if (value.length > 0){
                    return (
                      <React.Fragment key={ key }>
                        <span key={ key } className="betting-history__name">
                          {
                            (bet.prediction && (bet.prediction.type === 'cross_event' || isNaN(+key)))
                              ? `${ key } -`
                              : `${ toRoman(key) } -`
                          }
                        </span>
                        <span className="betting-history__position">{ _.isArray(value) ? value.join(',') : value }</span>
                      </React.Fragment>
                    );
                  }

                  return null;
                })
              }
            </div>
          }
          {
            (bet.productType === 'horses' && ((!bet.prediction || bet.prediction?.numbersToPredictCount === 1) && bet.oddsType !== 'tote')) &&
            <>
              <span className={
                classNames({
                  'betting-history__position' :bet.prediction && bet.prediction.numbersToPredictCount > 1,
                  'betting-history__name': !bet.prediction || bet.prediction?.numbersToPredictCount === 1
                })
              }
              >{ !_.isEmpty(jsonLabel) ? `${ jsonLabel.id }. ${ jsonLabel.competitor }` : bet.prediction.anyNumbers.join(',') }</span>
            </>
          }
          {
            bet.productType === 'numbers' && bet?.prediction?.anyNumbers &&
            <div className="betting-history__balls-container">
              <BallsContainer anyNumbers={ jsonLabel } sameBallset={ bet.numberBonus && bet.numberBonus.sameBallset } />
            </div>
          }
          {
            bet.prediction?.permutationsCount > 1 && (
              <button
                className="betting-history__combinations-wrapper"
                onClick={ () => setShowCombinations(true) }
              >
                { t('betslip.combinations') } { bet.prediction.permutationsCount }
              </button>
            )
          }
        </div>
        { /* <div className="betting-history__container betting-history__container--vertical">
          <span className="betting-history__score">8´: 2-0</span>
          <span className="betting-history__status">in-play</span>
        </div> */ }
        <div className="betting-history__container betting-history__container--vertical betting-history__container--spaced">
          <div className="betting-history__icons">
            <Icon
              className="betting-history__icon-result betting-history__icon-result--betline"
              icon={ status }
            />
            { renderLabelName(t(`betHistory.${ status }`)) }
          </div>
          {
            status === 'betCancelled' && (
              <div className="betting-history__icons">
                <Icon
                  className="betting-history__icon-result betting-history__icon-result--betline"
                  icon="fullStakeBack"
                />
                { renderLabelName(t('betHistory.fullStakeBack')) }
              </div>
            )
          }
          {
            status === 'halfStakeBackHalfWin' && (
              <>
                <div className="betting-history__icons">
                  <Icon
                    className="betting-history__icon-result betting-history__icon-result--betline"
                    icon="halfStakeBack"
                  />
                  { renderLabelName(t('betHistory.halfStakeBack')) }
                </div>
                <div className="betting-history__icons">
                  <Icon
                    className="betting-history__icon-result betting-history__icon-result--betline"
                    icon="partiallyPaid"
                  />
                  { renderLabelName(t('betHistory.partiallyPaid')) }
                </div>
              </>
            )
          }
        </div>
        <div className="betting-history__dropdown">
          {
            bet.odds &&
            <span
              className={
                classNames({
                  'betting-history__odds': true,
                  'betting-history__odds--line-through': bet.adjustedOdds && (+bet.adjustedOdds !== +bet.odds) && bet.statusLabel !== 'lost'
                })
              }
            >
              { formatOdds(bet.odds, selectedOddsType) }
              { renderLabelName(t('betHistory.originalOdds')) }
            </span>
          }
          {
            (bet.adjustedOdds && (+bet.adjustedOdds !== +bet.odds) && bet.statusLabel !== 'lost') &&
            <span
              className="betting-history__adjusted-odds"
            >
              { formatOdds(+bet.adjustedOdds, selectedOddsType) }
              { renderLabelName(t('betHistory.adjustedOdds')) }
            </span>
          }
          {
            bet.prediction?.permutations?.length > 0 &&
            _.find(bet.prediction.permutations, { is_winning: true }) && !bet.odds &&
            formatOdds(_.find(bet.prediction.permutations, { is_winning: true }).odds, selectedOddsType)
          }
        </div>
      </div>
      <div className="betting-history__more">
        <div className="betting-history__match">
          {
            bet?.rule4Enabled &&
            bet?.rule4Deduction &&
            bet?.oddsType === 'outcome' &&
            +bet?.voidFactor <= 1 &&
            bet?.statusLabel !== 'lost' &&
            renderRule4Deduction(bet)
          }
          { (+bet?.voidFactor <= 1 && +bet?.voidFactor !== 0) && renderVoid(bet) }
          {
            (!isSettled && bet?.rule4Enabled) &&
            bet?.oddsType === 'outcome' &&
            bet?.statusLabel !== 'lost' &&
            renderRule4Deduction(bet)
          }
          { +bet?.voidFactor > 1 && renderDeadHeat(bet) }
          { bet?.statusLabel === 'bet_cancelled' && renderCancelled() }
        </div>
        {
          showCombinations && (
            <>
              <CombinationsModal
                title={ t('numbers.combinations') }
                permutations={ bet.prediction.permutations }
                stake={ bet.prediction.singleStake / 100 }
                profit={ bet.prediction.minimumReturn / 100 }
                odds={ bet.odds }
                onClick={ () => setShowCombinations(!showCombinations) }
              />
            </>
          )
        }
      </div>
      <div className="betting-history__icon sport-icon">{ bet?.sportIcon }</div>
    </div>
  );
}
